import { WeightedPrompt } from "@/lib/lyria-client";
import { PromptMode } from "@/hooks/use-lyria";
import { Music, Zap, Plus, Shuffle } from "lucide-react";
import { cn } from "@/lib/utils";

interface ActivePromptDisplayProps {
  prompts: WeightedPrompt[];
  promptMode?: PromptMode;
  isPlaying: boolean;
}

function getPromptCardClass(weight: number): string {
  if (weight > 0.7) return "bg-plum/10 border-plum/30 text-foreground shadow-lg shadow-plum/5";
  if (weight >= 0.5) return "bg-white/5 border-white/10 text-foreground";
  return "bg-transparent border-white/5 text-muted-foreground opacity-80";
}

function getWeightBadgeClass(weight: number): string {
  if (weight > 0.7) return "bg-plum/20 text-plum-light";
  if (weight >= 0.5) return "bg-accent/20 text-accent";
  return "bg-white/5 text-muted-foreground";
}

export function ActivePromptDisplay({ prompts, promptMode, isPlaying }: ActivePromptDisplayProps) {
  if (prompts.length === 0) {
    return (
      <div className="soft-glass rounded-lg p-4 border border-white/5">
        <div className="flex items-center gap-3 text-muted-foreground text-xs uppercase tracking-widest font-medium">
          <Music className="w-4 h-4 opacity-50" />
          <span>No active synthesis prompts</span>
        </div>
      </div>
    );
  }

  return (
    <div className="soft-glass rounded-lg p-4 border border-white/10 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          <Zap className={cn(
            "w-3.5 h-3.5",
            isPlaying ? "text-accent animate-pulse" : "text-muted-foreground opacity-50"
          )} />
          <span>Active Prompts</span>
        </div>

        {/* Mode indicator */}
        {promptMode && promptMode !== 'replace' && (
          <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-white/5 border border-white/5 text-[10px] uppercase tracking-wider font-bold text-accent">
            {promptMode === 'additive' && (
              <>
                <Plus className="w-3 h-3" />
                <span>Layering</span>
              </>
            )}
            {promptMode === 'blend' && (
              <>
                <Shuffle className="w-3 h-3" />
                <span>Blending</span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {prompts.map((prompt, index) => (
          <div
            key={index}
            className={cn(
              "rounded-lg p-3 text-xs transition-all border",
              getPromptCardClass(prompt.weight)
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <p className="flex-1 leading-relaxed font-medium">{prompt.text}</p>
              <div className="shrink-0">
                <span className={cn(
                  "px-2 py-0.5 rounded-lg text-[10px] font-bold tracking-tighter",
                  getWeightBadgeClass(prompt.weight)
                )}>
                  {Math.round(prompt.weight * 100)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
